import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { PIN_ICONS } from '../../dialogs/pin-rework-dialogs/new-save-pin/new-save-pin.component';
import { NewDeletePinComponent } from '../../dialogs/pin-rework-dialogs/new-delete-pin/new-delete-pin.component';
import { SampleService } from '../../services/sample.service';
import { GroupService } from '../../services/group.service';
import { PinManagerService } from '../../services/pin-manager.service';
import { SeasonManagerService } from '../../services/season-manager.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError, Subscription, Subject } from 'rxjs';
import dayjs from 'dayjs';

@Component({
    selector: 'app-pin-manager',
    templateUrl: './pin-manager.component.html',
    styleUrls: ['./pin-manager.component.css'],
    standalone: false
})
export class PinManagerComponent implements OnInit, OnDestroy {
  pinIcons = PIN_ICONS;
  refreshPins$ = new Subject<string>();
  sidePanelOpen = false;
  sidePanelTitle = '';
  sidePanelPin: any = null;
  sidePanelSamples: any[] = [];
  panelMode: 'view' | 'addSample' | 'editSample' | 'editPin' = 'view';

  private routeSub: Subscription;

  // Add sample form
  sampleName = '';
  sampleDepth = '0';
  rockPercentage = '0';
  sampleNote = '';
  selectedGroupId: number = null;
  plantPartId: number = null;
  growthStageLinkId: number = null;

  groups: any[] = [];
  plantParts: any[] = [];
  growthStages: any[] = [];
  showGroupDropdown = false;
  selectedGroup: any = null;

  // Edit sample
  editingSample: any = null;
  editSampleName = '';
  editSampleDepth = '';
  editRockPercentage = '';
  editSampleNote = '';
  editSelectedGroup: any = null;
  editSelectedGroupId: number = null;
  editPlantPartId: number = null;
  editGrowthStageLinkId: number = null;
  editIsCompleted = false;
  showEditGroupDropdown = false;
  showGrowthStageDropdown = false;
  showPlantPartDropdown = false;

  // Edit pin
  editPinDescription = '';
  editYieldUnitId: number = null;
  editYieldEstimation: number = null;
  editYieldWeightedValue: number = null;
  editYieldEstimationDate = '';
  unitsOfMeasure: any[] = [];
  showUnitDropdown = false;

  constructor(
    private sampleService: SampleService,
    private groupService: GroupService,
    private pinManagerService: PinManagerService,
    private seasonManagerService: SeasonManagerService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.routeSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.closeSidePanel();
      }
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  formatDate(date: string): string {
    return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-';
  }

  openPinDetail(pin: any, samples: any[] = []) {
    this.sidePanelPin = pin;
    this.sidePanelTitle = (pin.pinType?.name || 'Pin') + ' Pin';
    this.sidePanelSamples = samples;
    this.sidePanelOpen = true;
    this.panelMode = 'view';

    // Populate editable fields for non-sample pins
    this.editPinDescription = pin.description || '';
    if (pin.pinType?.id === 8) {
      this.pinManagerService.getYieldestimationUnitOfmeasure().subscribe(res => this.unitsOfMeasure = res || []);
      this.pinManagerService.getYieldestimationPinById(pin.id).subscribe({
        next: (res) => {
          this.editYieldUnitId = res?.unitOfMeasureId || null;
          this.editYieldEstimation = res?.yieldEstimation || null;
          this.editYieldWeightedValue = res?.yieldWeightedValue || null;
          this.editYieldEstimationDate = res?.estimationDate ? dayjs(res.estimationDate).format('YYYY-MM-DD') : '';
        },
        error: () => {}
      });
    }

    // Load NTester data
    if (pin.pinType?.id === 9) {
      this.pinManagerService.getSavedNTesterPinById(pin.id).subscribe({
        next: (res: any) => {
          this.sidePanelPin.variety = res?.variety || null;
          this.sidePanelPin.crop = res?.crop || null;
          this.sidePanelPin.cultivar = res?.cultivar || null;
          this.sidePanelPin.growthStage = res?.growthStage || null;
          this.sidePanelPin.totalNitrogen = res?.totalNitrogen || null;
          this.sidePanelPin.medianValue = res?.medianValue || null;
          this.sidePanelPin.averageValue = res?.averageValue || null;
          this.sidePanelPin.yieldTarget = res?.yieldTarget || null;
          this.sidePanelPin.alreadyApplied = res?.alreadyApplied || null;
          this.sidePanelPin.proteinTarget = res?.proteinTarget || null;
          this.sidePanelPin.nRecommendation = res?.nRecommendation || null;
        },
        error: () => {}
      });
    }
  }

  closeSidePanel() {
    this.sidePanelOpen = false;
    this.sidePanelPin = null;
    this.sidePanelSamples = [];
    this.panelMode = 'view';
  }

  openAddSampleForm() {
    this.panelMode = 'addSample';
    this.sampleName = '';
    this.sampleDepth = '0';
    this.rockPercentage = '0';
    this.sampleNote = '';
    this.selectedGroup = null;
    this.plantPartId = null;
    this.growthStageLinkId = null;
    this.loadGroups();
    if (this.sidePanelPin?.pinType?.id === 3) {
      this.loadPlantParts();
    }
  }

  backToView() {
    this.panelMode = 'view';
  }

  loadGroups() {
    const tenantId = Number(sessionStorage.getItem('selectedTenant'));
    this.groupService.getGroups(tenantId).pipe(
      catchError(e => { console.error(e); return throwError(() => e); })
    ).subscribe(res => this.groups = res);
  }

  loadPlantParts() {
    this.sampleService.getSoftTissuePlantPartList().pipe(
      catchError(e => { console.error(e); return throwError(() => e); })
    ).subscribe(res => this.plantParts = res);
  }

  loadGrowthStages(fieldId: number) {
    this.seasonManagerService.getDetailsOfFieldById(fieldId).subscribe({
      next: (res) => {
        this.pinManagerService.getGrowthStageMetaData(res?.detail?.currentCrop).subscribe({
          next: (stages: any[]) => this.growthStages = stages || [],
          error: () => this.growthStages = []
        });
      },
      error: () => this.growthStages = []
    });
  }

  selectGroup(g: any) {
    this.selectedGroup = g;
    this.selectedGroupId = g.id;
    this.showGroupDropdown = false;
  }

  saveSample() {
    const pin = this.sidePanelPin;
    if (!pin || !this.sampleName || !this.selectedGroupId) return;

    if (pin.pinType.id === 4) {
      this.sampleService.postSoilSample(pin.id, this.selectedGroupId, this.sampleName, this.sampleDepth, this.rockPercentage, this.sampleNote)
        .subscribe(() => this.afterSampleEdit());
    } else if (pin.pinType.id === 3) {
      this.sampleService.postSoftTissueSample(pin.id, this.selectedGroupId, this.sampleName, this.plantPartId, this.growthStageLinkId, this.sampleNote)
        .subscribe(() => this.afterSampleEdit());
    }
  }

  navigateToSampleGroup(sample: any) {
    this.openEditSample(sample);
  }

  openEditSample(sample: any) {
    this.editingSample = sample;
    this.editSampleName = sample.name || '';
    this.editSampleNote = sample.note || '';
    this.editIsCompleted = sample.isCompleted || false;
    this.editSelectedGroup = sample.groupId ? { id: sample.groupId, name: sample.groupName } : null;
    this.editSelectedGroupId = sample.groupId || null;

    if (this.sidePanelPin?.pinType?.id === 4) {
      this.editSampleDepth = sample.soil?.sampleDepth || '';
      this.editRockPercentage = sample.soil?.rockPercentage || '';
    } else if (this.sidePanelPin?.pinType?.id === 3) {
      this.editPlantPartId = sample.softTissue?.plantPart?.id || null;
      this.editGrowthStageLinkId = sample.softTissue?.pinCropGrowthStageLinkId || null;
      this.loadPlantParts();
      const fieldId = this.sidePanelPin?.field?.id || this.sidePanelPin?.fieldId;
      if (fieldId) {
        this.loadGrowthStages(fieldId);
      }
    }

    this.loadGroups();
    this.panelMode = 'editSample';
  }

  selectEditGroup(g: any) {
    this.editSelectedGroup = g;
    this.editSelectedGroupId = g.id;
    this.showEditGroupDropdown = false;
  }

  getGrowthStageName(id: number): string {
    return this.growthStages.find(gs => gs.id === id)?.name || '';
  }

  getPlantPartName(id: number): string {
    return this.plantParts.find(pp => pp.id === id)?.name || '';
  }

  updateSample() {
    const pin = this.sidePanelPin;
    const sample = this.editingSample;
    if (!pin || !sample) return;

    if (pin.pinType.id === 4) {
      this.sampleService.updateSoilSample(
        pin.id, sample.id, this.editSelectedGroupId,
        this.editSampleName, this.editSampleDepth, this.editRockPercentage,
        this.editSampleNote, this.editIsCompleted
      ).subscribe(() => this.afterSampleEdit());
    } else if (pin.pinType.id === 3) {
      this.sampleService.updateSoftTissueSample(
        pin.id, sample.id, this.editSelectedGroupId,
        this.editSampleName, this.editPlantPartId, this.editGrowthStageLinkId,
        this.editSampleNote, this.editIsCompleted
      ).subscribe(() => this.afterSampleEdit());
    }
  }

  deleteSample() {
    if (!this.editingSample) return;
    const dialogRef = this.dialog.open(NewDeletePinComponent, {
      panelClass: 'compact-dialog',
      data: {
        isBulkDelete: true,
        description: this.editSampleName || 'Sample',
        field: this.sidePanelPin?.field?.name || '-'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.option === 'delete') {
        this.sampleService.deleteSampleById(this.editingSample.id)
          .subscribe(() => this.afterSampleEdit());
      }
    });
  }

  deletePin() {
    if (!this.sidePanelPin) return;
    const dialogRef = this.dialog.open(NewDeletePinComponent, {
      panelClass: 'compact-dialog',
      data: {
        pinId: this.sidePanelPin.id,
        isBulkDelete: false,
        description: this.sidePanelPin.description || 'Pin',
        field: this.sidePanelPin.field?.name || '-'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.option === 'delete') {
        this.closeSidePanel();
        this.refreshPins$.next('');
      }
    });
  }

  openEditPinForm() {
    this.editPinDescription = this.sidePanelPin?.description || '';
    this.panelMode = 'editPin';

    if (this.sidePanelPin?.pinType?.id === 8) {
      this.pinManagerService.getYieldestimationUnitOfmeasure().subscribe(res => this.unitsOfMeasure = res || []);
      this.pinManagerService.getYieldestimationPinById(this.sidePanelPin.id).subscribe({
        next: (res) => {
          this.editYieldUnitId = res?.unitOfMeasureId || null;
          this.editYieldEstimation = res?.yieldEstimation || null;
          this.editYieldWeightedValue = res?.yieldWeightedValue || null;
          this.editYieldEstimationDate = res?.estimationDate ? dayjs(res.estimationDate).format('YYYY-MM-DD') : '';
        },
        error: () => {}
      });
    }
  }

  updatePin() {
    if (!this.sidePanelPin || !this.editPinDescription) return;

    if (this.sidePanelPin.pinType?.id === 8) {
      this.pinManagerService.updateYieldEstimationPinById(
        this.sidePanelPin.id, this.editPinDescription,
        this.editYieldUnitId, this.editYieldEstimation,
        this.editYieldWeightedValue, this.editYieldEstimationDate
      ).subscribe(() => {
        this.sidePanelPin.description = this.editPinDescription;
        this.refreshPins$.next('Update Successful');
      });
    } else {
      this.pinManagerService.updatePinById(this.sidePanelPin.id, this.editPinDescription).subscribe(() => {
        this.sidePanelPin.description = this.editPinDescription;
        this.refreshPins$.next('Update Successful');
      });
    }
  }

  getUnitName(id: number): string {
    return this.unitsOfMeasure.find(u => u.id === id)?.name || '';
  }

  convertToVc() {
    if (!this.sidePanelPin) return;
    const dialogRef = this.dialog.open(NewDeletePinComponent, {
      panelClass: 'compact-dialog',
      data: {
        isBulkDelete: true,
        title: 'Convert Pin?',
        message: 'Are you sure you want to convert this pin to a Virtual Consultant?',
        confirmLabel: 'Convert',
        confirmColor: 'primary'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.option === 'delete') {
        this.pinManagerService.convertMarkerToVcPinById(this.sidePanelPin.id).subscribe(() => {
          this.closeSidePanel();
          this.refreshPins$.next('');
        });
      }
    });
  }

  openVirtualConsultant() {
    if (!this.sidePanelPin) return;
    const lat = this.sidePanelPin.lat;
    const lng = this.sidePanelPin.lng;
    this.closeSidePanel();
    this.router.navigateByUrl('/virtual-consultant', { state: { lat, lng } });
  }

  private afterSampleEdit() {
    this.panelMode = 'view';
    const pin = this.sidePanelPin;
    if (pin?.pinType?.id === 4) {
      this.sampleService.getSoilSamplesListByPinId(pin.id).pipe(
        catchError(e => { console.error(e); return throwError(() => e); })
      ).subscribe(samples => {
        this.sidePanelSamples = samples.map(s => ({ id: s.id, name: s.name, groupName: s.group?.name, groupId: s.group?.id, dateCreated: s.creationDate, note: s.note, isCompleted: s.isCompleted, soil: s.soil }));
      });
    } else if (pin?.pinType?.id === 3) {
      this.sampleService.getSoftTissueSamplesListByPinId(pin.id).pipe(
        catchError(e => { console.error(e); return throwError(() => e); })
      ).subscribe(samples => {
        this.sidePanelSamples = samples.map(s => ({ id: s.id, name: s.name, groupName: s.group?.name, groupId: s.group?.id, dateCreated: s.creationDate, note: s.note, isCompleted: s.isCompleted, softTissue: s.softTissue }));
      });
    }
  }
}
